import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';
import { CircleCheck, Pencil, Undo2 } from 'lucide-react';

import { Annotator } from '@/components/blocks/annotator/annotator';
import { ShelfDetailPredictionsPanel } from '@/components/blocks/shelf-detail/shelf-detail-predictions-panel';
import { usePanelSections } from '@/components/blocks/shelf-detail/use-panel-sections';
import { useShelfDetailWorkspace } from '@/components/blocks/shelf-detail/use-shelf-detail-workspace';
import { Button } from '@/components/ui/button';
import { useImageNaturalSize } from '@/hooks/use-image-natural-size';
import {
  Detection,
  detectionToAnnotation,
  makeAnnotationStyle,
} from '@/lib/annotations';

interface ShelfDetailWorkspaceProps {
  detections: Array<Detection>;
  imagePath: string;
  readonly?: boolean;
  onReEdit?: () => void;
  reEditPending?: boolean;
}
const route = getRouteApi('/app/shelves/$customerId/$submissionId/$shelveId');

/** Pixels per line for wheel events that report deltas in lines. */
const LINE_HEIGHT_PX = 16;

/**
 * Pixels per wheel delta unit. Firefox reports mouse wheel deltas in lines
 * (DOM_DELTA_LINE) rather than pixels; a page delta is one viewport height.
 */
const wheelDeltaUnitPx = (deltaMode: number, pageHeight: number): number => {
  if (deltaMode === WheelEvent.DOM_DELTA_LINE) return LINE_HEIGHT_PX;
  if (deltaMode === WheelEvent.DOM_DELTA_PAGE) return pageHeight;

  return 1;
};
export const ShelfDetailWorkspace: FC<ShelfDetailWorkspaceProps> = ({
  detections,
  imagePath,
  readonly = false,
  onReEdit,
  reEditPending = false,
}: ShelfDetailWorkspaceProps) => {
  const { t } = useTranslation();
  const { customerId, submissionId, shelveId } = route.useParams();
  const {
    annotations,
    selectedIds,
    selectedId,
    selectAnnotation,
    toggleAnnotation,
    selectAnnotations,
    onAnnotationsChange,
    labelSelectedAnnotation,
    deleteSelectedAnnotation,
    undoLastLabel,
    canUndo,
  } = useShelfDetailWorkspace(detections.map(detectionToAnnotation), {
    customerId: Number(customerId),
    submissionId,
    shelveId,
  });

  useEffect(() => {
    if (readonly) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const isDelete = event.key === 'Delete' || event.key === 'Backspace';
      // Plain Ctrl/Cmd+Z only — Shift+Z is the redo gesture, which we don't
      // support and must not treat as another undo.
      const isUndo =
        event.key.toLowerCase() === 'z' &&
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey;
      if (!isDelete && !isUndo) return;

      // A held Ctrl+Z would fire one undo per key-repeat, silently walking
      // back many history entries with no redo to recover them.
      if (isUndo && event.repeat) return;

      const target = event.target;
      const isTextTarget =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement);
      if (isTextTarget) return;

      event.preventDefault();
      if (isUndo) {
        undoLastLabel().catch(() => undefined);
      } else {
        deleteSelectedAnnotation().catch(() => undefined);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [readonly, deleteSelectedAnnotation, undoLastLabel]);
  const naturalSize = useImageNaturalSize(imagePath);
  // New identity per selection change → the annotator calls `setStyle` and
  // repaints, keeping the red highlight on the current selection set.
  const annotationStyle = useMemo(
    () => makeAnnotationStyle(selectedIds),
    [selectedIds]
  );
  const { openSections, setSectionOpen, openAll, closeAll } =
    usePanelSections();
  const handleSelectAnnotation = useCallback(
    (id: string | null) => {
      if (id && id !== selectedId) {
        openAll();
      }

      if (!id) {
        closeAll();
      }

      selectAnnotation(id);
    },
    [closeAll, openAll, selectAnnotation, selectedId]
  );

  const handleToggleAnnotation = useCallback(
    (id: string) => {
      const adding = !selectedIds.includes(id);
      if (adding) {
        openAll();
      } else if (selectedIds.length === 1) {
        // Toggling out the last selected box clears the selection entirely.
        closeAll();
      }

      toggleAnnotation(id);
    },
    [closeAll, openAll, selectedIds, toggleAnnotation]
  );

  // Rubber-band (Shift + drag) result: the ids merge into the selection.
  const handleSelectAnnotations = useCallback(
    (ids: Array<string>) => {
      const added = ids.filter((id) => !selectedIds.includes(id));
      if (added.length > 0) {
        openAll();
      }

      selectAnnotations(ids);
    },
    [openAll, selectAnnotations, selectedIds]
  );

  // The completed overlay blocks clicks on the panel (nothing there can act on
  // a completed shelf) but forwards the wheel so the list still scrolls. A
  // native non-passive listener is needed: React registers wheel handlers as
  // passive, so preventDefault there could not stop the page from scrolling.
  const panelListRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const scrollPanelList = (event: globalThis.WheelEvent) => {
      event.preventDefault();
      const list = panelListRef.current;
      if (!list) return;

      const unit = wheelDeltaUnitPx(event.deltaMode, list.clientHeight);

      list.scrollBy({
        top: event.deltaY * unit,
        left: event.deltaX * unit,
      });
    };

    overlay.addEventListener('wheel', scrollPanelList, { passive: false });

    return () => {
      overlay.removeEventListener('wheel', scrollPanelList);
    };
  }, [readonly]);

  return (
    <>
      <div className='flex justify-between gap-2 workspace-height'>
        <div className='flex flex-col w-3/5 2xl:w-2/3 3xl:w-3/4 overflow-hidden'>
          <Annotator
            className={'workspace-height'}
            image={imagePath}
            annotations={annotations}
            style={annotationStyle}
            readonly={readonly}
            onAnnotationsChange={onAnnotationsChange}
            onSelectAnnotation={handleSelectAnnotation}
            onToggleAnnotation={handleToggleAnnotation}
            onSelectAnnotations={handleSelectAnnotations}
            selectedIds={selectedIds}
            toolbarExtras={
              // The wrapper carries the tooltip: a disabled Button has
              // pointer-events: none, so its own title never triggers.
              <span
                className='inline-flex'
                title={t('shelfDetail.controls.undo')}>
                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  disabled={!canUndo}
                  aria-label={t('shelfDetail.controls.undo')}
                  onClick={() => {
                    undoLastLabel().catch(() => undefined);
                  }}>
                  <Undo2 />
                </Button>
              </span>
            }
          />
        </div>

        <div className='flex w-2/5 flex-col 2xl:w-1/3 3xl:w-1/4'>
          <div className='relative min-h-0 flex-1'>
            {readonly && (
              <div
                ref={overlayRef}
                className='absolute inset-0 z-10 bg-black/25'
              />
            )}
            <ShelfDetailPredictionsPanel
              className='h-full'
              listRef={panelListRef}
              annotations={annotations}
              imagePath={imagePath}
              naturalSize={naturalSize}
              openSections={openSections}
              onSectionOpenChange={setSectionOpen}
              selectedIds={selectedIds}
              selectedId={selectedId}
              labelSelectedAnnotation={
                readonly ? () => undefined : labelSelectedAnnotation
              }
            />
          </div>
          {readonly && (
            // In normal flow below the panel, so it never covers the end of
            // the scrolled list.
            <div className='flex items-center gap-3 border-t bg-card p-4'>
              <CircleCheck className='size-6 shrink-0 text-success' />
              <div className='flex min-w-0 flex-1 flex-col'>
                <span className='font-medium'>
                  {t('shelfDetail.controls.completedTitle')}
                </span>
                <span className='text-sm text-muted-foreground'>
                  {t('shelfDetail.controls.completedHint')}
                </span>
              </div>
              {onReEdit && (
                <Button
                  size='sm'
                  className='shrink-0'
                  disabled={reEditPending}
                  onClick={onReEdit}>
                  <Pencil />
                  {t('shelfDetail.controls.reEdit')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
